/* @flow */
import * as React from 'react';
import { computed, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';

import AutoCompleteModel from 'models/forms/AutoCompleteModel.js';

import UserStore from 'stores/UserStore';
import ElectroTextField from '../components/inputs/ElectroTextField/ElectroTextField';
import ElectroSelectExtra from '../components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import FormWrapper from '../../components/FormWrapper/FormWrapper';
import FormSection from '../../components/FormSection/FormSection';
import FormControls from '../components/controls/FormControls/FormControls';
import ElectroResponseMessage from 'forms/components/inputs/ElectroResponseMessage';

import { autoCompleteHelpFor } from 'api/forms.js';

import { makeFields, supportCreate } from './setupSupportForm';

@translate()
@inject('userStore')
@observer
export default class SupportForm extends React.Component<*> {
  /**
   * Model that holds the list of options available for the "I want help for"
   * dropdown, this property will be instantiated in the constructor.
   */
  @observable autoCompleteHelpFor: AutoCompleteModel;

  @observable localState = {
    selectedValue: '',
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
  };

  @computed get form(): Object {
    const fields: Array<*> = makeFields(this.props.location.state, this.props.t);
    return supportCreate(fields, this.localState, this.props);
  }

  constructor(props) {
    super(props);

    // Instantiate the model for the dropdown options, note that
    // we need to specify whether to show the CAAS option or
    // not based on the user's language.
    this.autoCompleteHelpFor = new AutoCompleteModel(
      autoCompleteHelpFor(this.isUserLanguageEN(props.userStore))
    );
  }

  componentDidMount() {
    this.localState.selectedValue =
      (this.props.location && this.props.location.state && this.props.location.state.id) || null;
  }

  componentDidUpdate() {
    // Whenever this component updates we have to make sure the dropdown
    // options are also updated to add/remove the CAAS option based on
    // the user's language by updating the model's source function,
    // then we call loadData() to refresh the options.
    this.autoCompleteHelpFor.source = autoCompleteHelpFor(
      this.isUserLanguageEN(this.props.userStore)
    );
    this.autoCompleteHelpFor.loadData();
  }

  /**
   * Returns true if the given userStore's userLanguage setting is "en".
   */
  isUserLanguageEN(userStore: UserStore) {
    return userStore.userLanguage === 'en';
  }

  handleOnChangeReason = (selectedValue: string) => {
    this.localState.selectedValue = selectedValue;
  };

  render(): React.Element<*> {
    const form: Object = this.form;

    return (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <ElectroResponseMessage localState={this.localState} />
          <FormWrapper form={form}>
            <FormSection form={form} section="support">
              <ElectroSelectExtra
                field={form.$('support.askHelpFor')}
                form={form}
                formName="support"
                labelKey="id"
                multi={false}
                handleOnChange={this.handleOnChangeReason}
                singleSelectedValue={this.localState.selectedValue}
                props={{
                  model: this.autoCompleteHelpFor,
                }}
              />
              <ElectroTextField
                field={form.$('support.comments')}
                props={{
                  multiline: true,
                  rows: 7,
                }}
              />
            </FormSection>
            <FormControls form={form} />
          </FormWrapper>
        </PageWrapper>
      </React.Fragment>
    );
  }
}
